import React, { Children, useState } from 'react';
import { css, styled } from '@compiled/react';
import { CustomThemeButton, type CustomThemeButtonProps } from '@atlaskit/button';
import InlineDialog from '@atlaskit/inline-dialog';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import UrlIcon from '../../../../../common/ui/url-icon/index.tsx';
import messages from './messages';

export type Props = {
	name: string;
	description: string;
	iconUrl: string;
	groupCount: number;
	getGroupNames: () => string[];
};

const DialogContent = ({ children }: { children: () => string[] }) => {
	const { formatMessage } = useIntl();

	return (
		<Dialog>
			<DialogHeader>{formatMessage(messages.dialogTitle)}:</DialogHeader>
			{Children.map(children(), (name) => (
				<DialogItem>{name}</DialogItem>
			))}
		</Dialog>
	);
};

const Item = ({ name, description, iconUrl, groupCount, getGroupNames }: Props) => {
	const { formatMessage } = useIntl();
	const [isOpen, setOpen] = useState<boolean>(false);

	return (
		<Wrapper>
			<UrlIcon url={iconUrl} alt={name} />
			<ContentWrapper>
				<Title title={name}>{name}</Title>
				<Description title={description}>{description}</Description>
			</ContentWrapper>
			{groupCount ? (
				<InlineDialog content={<DialogContent>{getGroupNames}</DialogContent>} isOpen={isOpen}>
					<CustomThemeButton
						theme={getButtonTheme}
						onMouseEnter={() => {
							setOpen(true);
						}}
						onMouseLeave={() => {
							setOpen(false);
						}}
					>
						{formatMessage(messages.group, { count: groupCount })}
					</CustomThemeButton>
				</InlineDialog>
			) : null}
		</Wrapper>
	);
};

Item.defaultProps = {
	name: '',
	description: '',
	iconUrl: '',
	groupCount: 0,
	getGroupNames: () => [],
};

export default Item;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
});

const truncatableTitleStyleCompiledStyles = css({
	flexGrow: 1,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Description = styled.div(truncatableTitleStyleCompiledStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 2.5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Title = styled.div(truncatableTitleStyleCompiledStyles, {
	font: token('font.body.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 3}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	overflow: 'hidden',
	flexGrow: 1,
	margin: `0 ${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Dialog = styled.div({
	width: '250px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogHeader = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 2.5}px`,
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const DialogItem = styled.div(truncatableTitleStyleCompiledStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 3}px`,
});

const getButtonTheme: CustomThemeButtonProps['theme'] = (currentTheme, props) => {
	const { buttonStyles, ...rest } = currentTheme(props);

	return {
		...rest,
		buttonStyles: {
			backgroundColor: `${token('color.background.neutral', colors.N40)}`,
			color: token('color.text', colors.N600),
			font: token('font.body.UNSAFE_small'),
			border: 'none',
			borderRadius: `${gridSize * 1.5}px`,
			lineHeight: `${gridSize * 3}px`,
			width: 80,
			cursor: buttonStyles.cursor,
			alignSelf: 'center',
			flexShrink: 0,

			'&:hover': {
				backgroundColor: token('color.background.neutral.bold.hovered', colors.N400),
				color: token('color.text.inverse', colors.N0),
			},
		},
	};
};
